import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Link, useLocation, useNavigate, stripLang } from '../../router';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Menu, X } from 'lucide-react';
import LanguageFlags from '../CountryFlags';
import './Navbar.css';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [showLanguages, setShowLanguages] = useState(false);
    const menuButtonRef = useRef(null);
    const menuRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation(['navbar', 'common']);
    // Active-state checks compare against the logical path (no language prefix).
    const path = stripLang(location.pathname);
    const overlaysHero =
        path === '/' ||
        path.startsWith('/apartments/') ||
        path.startsWith('/local-guide');

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const closeMenu = useCallback(() => {
        setIsOpen(false);
        setShowLanguages(false);
    }, []);

    const closeMenuBeforeNavigation = useCallback(() => {
        flushSync(() => {
            closeMenu();
        });
    }, [closeMenu]);

    useLayoutEffect(() => {
        closeMenu();
    }, [location.pathname, location.search, location.hash, closeMenu]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const scrollY = window.scrollY;
        const { body } = document;
        const originalStyle = {
            overflow: body.style.overflow,
            position: body.style.position,
            top: body.style.top,
            width: body.style.width
        };

        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';

        return () => {
            body.style.overflow = originalStyle.overflow;
            body.style.position = originalStyle.position;
            body.style.top = originalStyle.top;
            body.style.width = originalStyle.width;
            window.scrollTo(0, scrollY);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeMenu();
                menuButtonRef.current?.focus();
                return;
            }

            if (event.key !== 'Tab' || !menuRef.current) {
                return;
            }

            const menuItems = Array.from(
                menuRef.current.querySelectorAll('a[href], button:not([disabled])')
            );
            const focusableItems = [menuButtonRef.current, ...menuItems].filter(Boolean);
            const firstItem = focusableItems[0];
            const lastItem = focusableItems[focusableItems.length - 1];

            if (!firstItem || !lastItem) {
                return;
            }

            if (event.shiftKey && document.activeElement === firstItem) {
                event.preventDefault();
                lastItem.focus();
            } else if (!event.shiftKey && document.activeElement === lastItem) {
                event.preventDefault();
                firstItem.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, closeMenu]);

    const changeLanguage = (lng) => {
        // Switch language by moving to the same page under the new language
        // prefix (e.g. /en/about → /de/about), preserving query/hash. The URL is
        // the source of truth; LanguageSync then aligns i18n, but set it here too
        // so the UI updates immediately.
        const target = `/${lng}${path === '/' ? '' : path}${location.search}${location.hash}`;
        closeMenuBeforeNavigation();
        navigate(target);
        i18n.changeLanguage(lng);
    };

    const toggleLanguages = (e) => {
        e.preventDefault();
        setShowLanguages(!showLanguages);
    };

    const languages = [
        { code: 'en', name: t('languageSelector.languages.en') },
        { code: 'de', name: t('languageSelector.languages.de') },
        { code: 'es', name: t('languageSelector.languages.es') },
        { code: 'fr', name: t('languageSelector.languages.fr') },
        { code: 'it', name: t('languageSelector.languages.it') },
        { code: 'pt', name: t('languageSelector.languages.pt') }
    ];

    return (
        <nav className={`navbar ${overlaysHero ? 'overlay' : ''} ${scrolled ? 'scrolled' : ''} ${isOpen ? 'menu-open' : ''}`}>
            <div className="navbar-container">
                <Link to="/" className="navbar-logo" onClick={closeMenuBeforeNavigation}>
                    {t('brand')}
                </Link>
                <button
                    type="button"
                    className="menu-icon"
                    ref={menuButtonRef}
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label={isOpen ? t('navbar:menu.close') : t('navbar:menu.open')}
                    aria-expanded={isOpen}
                    aria-controls="primary-navigation"
                >
                    {isOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
                </button>
                <button
                    type="button"
                    className={isOpen ? 'nav-backdrop active' : 'nav-backdrop'}
                    onClick={closeMenu}
                    tabIndex={-1}
                    aria-hidden="true"
                />
                <ul
                    id="primary-navigation"
                    className={isOpen ? 'nav-menu active' : 'nav-menu'}
                    ref={menuRef}
                >
                    <li className="nav-item">
                        <Link
                            to="/"
                            className={`nav-links ${path === '/' ? 'active' : ''}`}
                            onClick={closeMenuBeforeNavigation}
                        >
                            {t('menu.home')}
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link
                            to="/about"
                            className={`nav-links ${path === '/about' ? 'active' : ''}`}
                            onClick={closeMenuBeforeNavigation}
                        >
                            {t('menu.about')}
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link
                            to="/apartments"
                            className={`nav-links ${path.startsWith('/apartments') ? 'active' : ''}`}
                            onClick={closeMenuBeforeNavigation}
                        >
                            {t('menu.apartments')}
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link
                            to="/gallery"
                            className={`nav-links ${path === '/gallery' ? 'active' : ''}`}
                            onClick={closeMenuBeforeNavigation}
                        >
                            {t('menu.gallery')}
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link
                            to="/pricing"
                            className={`nav-links ${path === '/pricing' ? 'active' : ''}`}
                            onClick={closeMenuBeforeNavigation}
                        >
                            {t('menu.pricing')}
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link
                            to="/amenities"
                            className={`nav-links ${path === '/amenities' ? 'active' : ''}`}
                            onClick={closeMenuBeforeNavigation}
                        >
                            {t('menu.amenities')}
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link
                            to="/local-guide"
                            className={`nav-links ${path.startsWith('/local-guide') ? 'active' : ''}`}
                            onClick={closeMenuBeforeNavigation}
                        >
                            {t('menu.localGuide')}
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link
                            to="/contact"
                            className={`nav-links nav-cta ${path === '/contact' ? 'active' : ''}`}
                            onClick={closeMenuBeforeNavigation}
                        >
                            {t('common:cta.requestToBook')}
                        </Link>
                    </li>
                    <li className="nav-item language-dropdown">
                        <button
                            type="button"
                            className="nav-links language-toggle"
                            onClick={toggleLanguages}
                            aria-label={t('navbar:languageSelector.label')}
                            aria-expanded={showLanguages}
                        >
                            <span className="language-toggle-current">
                                <LanguageFlags language={i18n.language} />
                                <span className="language-toggle-label">{t('languageSelector.label')}</span>
                            </span>
                            {showLanguages ? (
                                <ChevronUp className="language-icon" size={14} aria-hidden="true" />
                            ) : (
                                <ChevronDown className="language-icon" size={14} aria-hidden="true" />
                            )}
                        </button>
                        {showLanguages && (
                            <div className="language-options">
                                {languages.map((lang) => (
                                    <button
                                        key={lang.code}
                                        onClick={() => changeLanguage(lang.code)}
                                        className={i18n.language === lang.code ? 'active' : ''}
                                        aria-label={lang.name}
                                        title={lang.name}
                                    >
                                        <LanguageFlags language={lang.code} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </li>
                </ul>
            </div>
        </nav>
    );
};

export default Navbar;
